import { useEffect, useState, useRef } from 'react';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const useAutoSave = (content: string, delay: number = 2000) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (content.trim() === '') {
      setSaveStatus('idle');
      return;
    }

    setSaveStatus('saving');

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      try {
        // Save to localStorage
        localStorage.setItem('prompt-editor-current', content);
        
        // Save to version history (every 5 minutes or significant changes)
        const lastSave = localStorage.getItem('prompt-editor-last-save');
        const lastSaveTime = lastSave ? parseInt(lastSave) : 0;
        const now = Date.now();
        
        if (now - lastSaveTime > 300000) { // 5 minutes
          saveToHistory(content);
          localStorage.setItem('prompt-editor-last-save', now.toString());
        }
        
        setSaveStatus('saved');
        
        // Reset to idle after 2 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);
        
      } catch (error) {
        setSaveStatus('error');
        console.error('Auto-save failed:', error);
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [content, delay]);

  const saveToHistory = (content: string) => {
    try {
      const existingHistory = localStorage.getItem('prompt-editor-history');
      const history = existingHistory ? JSON.parse(existingHistory) : [];
      
      const newVersion = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        content,
        timestamp: Date.now(),
        preview: content.substring(0, 150) + (content.length > 150 ? '...' : ''),
      };
      
      // Keep only last 20 versions
      const updatedHistory = [newVersion, ...history].slice(0, 20);
      
      localStorage.setItem('prompt-editor-history', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Failed to save to history:', error);
    }
  };

  // Load saved content on mount
  useEffect(() => {
    const saved = localStorage.getItem('prompt-editor-current');
    if (saved && content === '') {
      // This would need to be handled by the parent component
      // since we can't modify state from here
    }
  }, [content]);

  return { saveStatus };
};